import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Trash2, LogOut, Globe } from 'lucide-react'
import { useAuthStore } from '@/stores/useAuthStore'
import {
  listMyDocuments,
  createDocument,
  deleteDocument,
  type DocumentSummary,
} from '../lib/documents'

export function DocumentList() {
  const navigate = useNavigate()
  const signOut = useAuthStore((s) => s.signOut)
  const user = useAuthStore((s) => s.user)

  const [docs, setDocs] = useState<DocumentSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const list = await listMyDocuments()
        if (active) setDocs(list)
      } catch (e) {
        if (active) setError((e as Error).message)
      }
    })()
    return () => {
      active = false
    }
  }, [])

  const handleCreate = async () => {
    setCreating(true)
    try {
      const doc = await createDocument({
        content: {
          project: 'Untitled',
          tags: ['sample', 'json-graph'],
          settings: { theme: 'light', autosave: true },
          people: [
            { name: 'A', role: 'writer' },
            { name: 'B', role: 'editor' },
          ],
        },
      })
      navigate(`/graph/${doc.id}`)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this document?')) return
    try {
      await deleteDocument(id)
      setDocs((prev) => prev?.filter((d) => d.id !== id) ?? null)
    } catch (e) {
      setError((e as Error).message)
    }
  }

  return (
    <div className="h-dvh w-full flex flex-col bg-background overflow-hidden">
      <header className="flex-shrink-0 h-12 border-b border-border flex items-center justify-between px-6">
        <div
          className="text-[12px] font-medium tracking-tight"
          style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
        >
          JSON Graph
        </div>
        <div className="flex items-center gap-4">
          <span
            className="text-[11px] text-muted-foreground font-light"
            style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
          >
            {user?.email}
          </span>
          <button
            onClick={() => signOut().then(() => navigate('/login'))}
            className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground font-light"
            style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
          >
            <LogOut className="w-3 h-3" strokeWidth={1.5} />
            Sign out
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 md:px-8 py-12">
          <div className="flex items-center justify-between mb-8">
            <h1
              className="text-2xl font-light tracking-tight"
              style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
            >
              Your documents
            </h1>
            <button
              onClick={handleCreate}
              disabled={creating}
              className="flex items-center gap-1.5 bg-foreground text-background px-4 py-2 rounded-md text-sm font-light hover:bg-foreground/90 disabled:opacity-50"
              style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
              {creating ? 'Creating…' : 'New document'}
            </button>
          </div>

          {error && (
            <div className="mb-6 px-4 py-3 border border-destructive/20 bg-destructive/5 text-[12px] text-destructive font-light rounded-md">
              {error}
            </div>
          )}

          {docs === null && !error && (
            <div className="text-[12px] text-muted-foreground font-light">Loading…</div>
          )}

          {docs && docs.length === 0 && (
            <div className="border border-border rounded-md px-6 py-10 text-center">
              <p
                className="text-[14px] text-muted-foreground font-light mb-4"
                style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
              >
                No documents yet.
              </p>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="text-[12px] underline underline-offset-4 text-foreground hover:text-foreground/70 font-light"
                style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
              >
                Create your first one
              </button>
            </div>
          )}

          {docs && docs.length > 0 && (
            <ul className="divide-y divide-border border-t border-b border-border">
              {docs.map((d) => (
                <li
                  key={d.id}
                  className="group flex items-center gap-4 px-2 py-4 hover:bg-muted/30 cursor-pointer"
                  onClick={() => navigate(`/graph/${d.id}`)}
                >
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-[14px] font-medium tracking-tight truncate"
                      style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
                    >
                      {d.title || 'Untitled'}
                    </div>
                    <div
                      className="text-[11px] text-muted-foreground font-light mt-0.5"
                      style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
                    >
                      Updated {new Date(d.updated_at).toLocaleString()}
                    </div>
                  </div>
                  {d.is_public && d.share_slug && (
                    <div
                      className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground font-light"
                      style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
                      title={`Public at /g/${d.share_slug}`}
                    >
                      <Globe className="w-3 h-3" strokeWidth={1.5} />
                      Public
                    </div>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDelete(d.id)
                    }}
                    className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive p-1.5"
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
